import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Axios from "axios";
import { DATABASE_URL } from "../../appConstants";
import AuthHeader from "../../services/AuthHeader";
import {
  DataGrid,
  GridLinkOperator,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import { useAuthContext } from "../../hooks/useAuthContext";
import { Button } from "@mui/material";
import * as FaIcons from "react-icons/fa";
import { useLoadingContext } from "../../hooks/useLoadingContext";

const defaultFilterModel = {
  items: [],
  linkOperator: GridLinkOperator.And,
};

const defaultSortModel = [{ field: "id", sort: "asc" }];

const CustomToolbar = () => (
  <GridToolbarContainer>
    <div
      style={{
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        alignItems: "center",
        padding: "0 10px",
      }}
    >
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </div>
  </GridToolbarContainer>
);

function Graded() {
  const { user } = useAuthContext();
  const [gradingList, setGradingList] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [filterModel, setFilterModel] = useState(defaultFilterModel);
  const [appliedFilterModel, setAppliedFilterModel] = useState(
    defaultFilterModel
  );
  const [sortModel, setSortModel] = useState(defaultSortModel);
  const isInitialFilterRender = useRef(true);
  const navigate = useNavigate();
  const { isLoading, startLoading, stopLoading } = useLoadingContext();

  const columns = [
    { field: "id", headerName: "Grading ID", flex: 1 },
    { field: "candidate_full_name", headerName: "Candidate", flex: 1 },
    { field: "examiner_full_name", headerName: "Examiner", flex: 1 },
    { field: "question_type_name", headerName: "Question Type", flex: 1 },
    { field: "grade", headerName: "Grade", flex: 1 },
    { field: "comments", headerName: "Comments", flex: 1 },
    {
      field: "actions",
      type: "actions",
      flex: 1,
      sortable: false,
      filterable: false,
      headerName: "View",
      getActions: (params) => [
        <Button onClick={viewDetails(params.id)}>
          <FaIcons.FaEye />
        </Button>,
      ],
    },
  ];

  const viewDetails = useCallback(
    (id) => () => {
      navigate(id.toString());
    },
    []
  );

  useEffect(() => {
    if (isInitialFilterRender.current) {
      isInitialFilterRender.current = false;
      return;
    }

    const debounceFilter = setTimeout(() => {
      setPage(1);
      setAppliedFilterModel(filterModel);
    }, 500);

    return () => clearTimeout(debounceFilter);
  }, [filterModel]);

  useEffect(() => {
    startLoading();

    const encodedFilterModel = encodeURIComponent(
      JSON.stringify(appliedFilterModel)
    );
    const encodedSortModel = encodeURIComponent(JSON.stringify(sortModel));
    Axios.get(
      `${DATABASE_URL}/grading/graded/${user.test_center}?page=${page}&pageSize=${pageSize}&filterModel=${encodedFilterModel}&sortModel=${encodedSortModel}`,
      {
        headers: AuthHeader(),
      }
    ).then((response) => {
      let filteredList = response.data.final_grades;
      setGradingList(filteredList);
      setTotalRows(response.data.count);
      stopLoading();
    }).catch((error) => {
      console.log('Error: ', error);
      stopLoading();
    });
  }, [page, pageSize, appliedFilterModel, sortModel]);
  
  return (
    <div>
      <h1>Graded</h1>
      <div style={{ height: 600, width: "100%" }}>
        <DataGrid
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(1);
          }}
          page={page - 1}
          onPageChange={(newPage) => setPage(newPage + 1)}
          rowsPerPageOptions={[10, 25, 50]}
          pagination
          paginationMode="server"
          rowCount={totalRows}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={(newFilterModel) => {
            setFilterModel(newFilterModel);
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => {
            setPage(1);
            setSortModel(newSortModel);
          }}
          components={{
            Toolbar: CustomToolbar,
          }}
          loading={isLoading}
          rows={gradingList}
          columns={columns}
        />
      </div>
    </div>
  );
}

export default Graded;
